/**
 * Map known English notification / system message bodies to i18n keys.
 * Dynamic amounts/symbols are preserved via patterns; unknown text passes through.
 * UI-only — does not change API payloads or DB.
 */
export function localizeNotificationMessage(message, t) {
  if (message == null || message === '') return message;
  const raw = String(message)
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .trim();

  const exact = {
    'Position closed successfully!': t('notifications.msg.positionClosed'),
    'New trading day! Account unlocked.': t('notifications.msg.tradingDayUnlocked'),
    'Daily profit limit reached. Trading resumes tomorrow.': t('notifications.msg.dailyProfitLock'),
    'Your challenge account has ended. Please log in again.': t('notifications.msg.challengeEndedLogin'),
    'Phase passed! Check your email for new account credentials.': t('notifications.msg.phasePassedEmail'),
    'Challenge has been activated': t('notifications.msg.challengeActivated'),
    'Account has been Liquidated!': t('notifications.msg.accountLiquidated'),
    'Bank account details received': t('notifications.msg.bankDetailsReceived'),
    'New system notification received!': t('notifications.msg.newSystemNotification'),
    '🏆 Congratulations! You have completed your challenge!': t('notifications.msg.challengeCongrats'),
    'Congratulations! You have completed your challenge!': t('notifications.msg.challengeCongrats'),
    'Returning to parent account...': t('notifications.msg.returningToParent'),
  };
  if (exact[raw]) return exact[raw];

  let m;

  // Margin call (incl. scientific notation levels)
  m = raw.match(/^Account is Approaching Liquidation!\s*Margin Level:\s*(.+)%$/i);
  if (m) {
    return t('notifications.msg.marginCallLevel', { level: m[1].trim() });
  }

  // SL / TP hit — optional trailing dot on symbol (dump: ETHUSD.)
  m = raw.match(/^(TP|SL)\s+hit for position\s+(.+?)\s+at price\s+(.+)$/i);
  if (m) {
    const kind = m[1].toUpperCase() === 'TP' ? 'tpHit' : 'slHit';
    return t(`notifications.msg.${kind}`, {
      symbol: m[2].trim(),
      price: m[3].trim(),
    });
  }

  m = raw.match(/^Deposit request of (.+)$/i);
  if (m) {
    return t('notifications.msg.depositRequest', { amount: m[1].trim() });
  }
  m = raw.match(/^Withdraw request of (.+)$/i);
  if (m) {
    return t('notifications.msg.withdrawRequest', { amount: m[1].trim() });
  }

  m = raw.match(/^Challenge payment request of (.+)$/i);
  if (m) {
    return t('notifications.msg.challengePaymentRequest', { amount: m[1].trim() });
  }

  // "A Challenge (ref) has been activated" and "Challenge (ref) has been activated"
  m = raw.match(/^A\s+Challenge\s+\((.+)\)\s+has been activated$/i);
  if (m) {
    return t('notifications.msg.challengeActivatedRef', { ref: m[1].trim() });
  }
  m = raw.match(/^Challenge\s+\((.+)\)\s+has been activated$/i);
  if (m) {
    return t('notifications.msg.challengeActivatedRef', { ref: m[1].trim() });
  }

  m = raw.match(/^Challenge Phase Passed! Moving to next phase\. Equity: (.+)$/i);
  if (m) {
    return t('notifications.msg.phasePassedEquity', { equity: m[1].trim() });
  }

  m = raw.match(/^(?:🏆\s*)?Challenge Completed! All phases passed successfully\. Equity: (.+)$/i);
  if (m) {
    return t('notifications.msg.challengeCompletedEquity', { equity: m[1].trim() });
  }

  m = raw.match(/^Challenge Failed: Overall loss limit breached\. Equity: (.+)$/i);
  if (m) {
    return t('notifications.msg.challengeFailedOverall', { equity: m[1].trim() });
  }

  m = raw.match(/^Challenge Failed: Daily loss limit breached\. Loss: (.+)$/i);
  if (m) {
    return t('notifications.msg.challengeFailedDaily', { loss: m[1].trim() });
  }

  m = raw.match(/^Challenge Failed: Duration expired\. Elapsed days: (.+)$/i);
  if (m) {
    return t('notifications.msg.challengeFailedDuration', { days: m[1].trim() });
  }

  // Ticket / feedback templates from dtrader-lv
  m = raw.match(/^New message in (.+) from (.+): (.+)$/i);
  if (m) {
    return t('notifications.msg.ticketMessage', {
      title: m[1].trim(),
      sender: m[2].trim(),
      preview: m[3].trim(),
    });
  }

  m = raw.match(/^The status of your feedback,\s*(.+),\s*is updated to\s*(.+)\.?$/i);
  if (m) {
    return t('notifications.msg.feedbackStatusUpdated', {
      title: m[1].trim(),
      status: m[2].trim(),
    });
  }

  m = raw.match(/^New feedback received from (.+) with the title (.+)$/i);
  if (m) {
    return t('notifications.msg.feedbackCreated', {
      user: m[1].trim(),
      title: m[2].trim(),
    });
  }

  return raw;
}
